import Map, { CircleLayer, Layer, MapLayerMouseEvent, MapRef, Marker, Source } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";

import { Airport } from "@/models/airport";
import { formatDistanceAway } from "@/daily/game";
import { dt } from "@/daily/strings";
import {
  bearing,
  interpolateGreatCircle,
  nearestLongitude,
  unwrapLongitudes,
} from "@/daily/geo";
import { useProgress } from "@/daily/hooks";
import { LANDCOVER_COLOUR, paintMapColours } from "@/daily/mapColours";
import { useMarkedMotion } from "@/daily/useMarkedMotion";
import { DailyGuess, DailyPuzzle } from "@/daily/types";
import { PlaneIcon } from "./icons";

// Same public token the classic game's GameMap uses.
const MAPBOX_TOKEN =
  "pk.eyJ1Ijoia2lyYW5wYW5lc2FyIiwiYSI6ImNreHJyNTdlazBrb2QycG8wcGd4bmIxNmkifQ.WC9rlsZlW039F2ydh32vSw";

// The base style, recoloured by paintMapColours.
const MAP_STYLE = "mapbox://styles/mapbox/light-v11";

const PROBE_DRAWS_FOR = 1950;
const LANDING_TAKES = 1200;
// How far into the landing the plane's heading stops following its curve.
const LANDED_AT = 0.99;
const PROBE_STEPS = 32;

// How close to the edge of the map a guess can sit before the view moves to
// bring it in, and the space the top bar and guess card take up (px).
const EDGE_MARGIN = 64;
const TOP_BAR_HEIGHT = 84;
const CARD_HEIGHT = 320;
const CARD_WIDTH_DESKTOP = 448;

// How the marked airports' rings are drawn. Each carries its own size and
// strength, which change as they pop in and fade (useMarkedMotion). Their
// codes are labels like the guesses' (see the markers below).
const MARKED_RING: CircleLayer["paint"] = {
  "circle-radius": ["*", 7, ["get", "scale"]],
  "circle-color": "#FBF6EC",
  "circle-stroke-color": "#B83A26",
  "circle-stroke-width": ["*", 3, ["get", "scale"]],
  "circle-opacity": ["get", "opacity"],
  "circle-stroke-opacity": ["get", "opacity"],
};

// The line that reaches from the plane toward a guess while it is checked.
export interface Probe {
  airport: Airport;
  state: "drawing" | "wrong" | "correct";
}

interface DailyMapProps {
  puzzle: DailyPuzzle;
  // Guesses whose result has been shown.
  guesses: DailyGuess[];
  probe: Probe | null;
  // A correct guess: the plane flies the rest of the way in.
  landing: boolean;
  // A guess's pin was tapped: show how far off it was again.
  onGuessClick: (guess: DailyGuess) => void;
  // The plane asks where it is landing, until the first guess goes in.
  asking: boolean;
  isDesktop: boolean;
  // Airports marked by the possible-airports clue, the
  // destination among them. Empty until that clue is out.
  possibleAirports: Airport[];
  // Whether a guess can be made right now.
  canGuess: boolean;
  onGuessMarked: (airport: Airport) => void;
}

// A dotted light grey line while the guess is checked and if it misses;
// solid green if it is right.
const PROBE_COLOUR = {
  drawing: "#A9A498",
  wrong: "#A9A498",
  correct: "#2E7D4F",
};

export const DailyMap = ({
  puzzle,
  guesses,
  probe,
  landing,
  onGuessClick,
  asking,
  isDesktop,
  possibleAirports,
  canGuess,
  onGuessMarked,
}: DailyMapProps) => {
  const mapRef = useRef<MapRef>(null);
  const [loaded, setLoaded] = useState(false);
  const painted = useRef(false);

  // Lines are kept in one piece across the antimeridian (see geo.ts).
  const trackCoordinates = useMemo(
    () => unwrapLongitudes(puzzle.track.map((point) => [point.lon, point.lat])),
    [puzzle]
  );
  // Where the plane is, on the same run of longitudes as its flight path.
  const planeLon = trackCoordinates[trackCoordinates.length - 1][0];
  const planeLat = puzzle.position.lat;
  const wrongGuesses = guesses.filter((guess) => guess.band !== "correct");

  const probeIata = probe?.airport.iata ?? null;
  const probeProgress = useProgress(probeIata, PROBE_DRAWS_FOR);
  const landingProgress = useProgress(landing ? "landing" : null, LANDING_TAKES);

  // The probe grows from the plane toward the guessed airport.
  const probeCoordinates = useMemo(() => {
    if (!probe) return [];
    const points = Array.from({ length: PROBE_STEPS + 1 }, (_, index) => {
      const point = interpolateGreatCircle(
        puzzle.position,
        probe.airport,
        (index / PROBE_STEPS) * probeProgress
      );
      return [point.lon, point.lat];
    });
    return unwrapLongitudes(points, planeLon);
  }, [probe, probeProgress, puzzle, planeLon]);

  const planePosition = landing
    ? interpolateGreatCircle(puzzle.position, puzzle.destination, landingProgress)
    : puzzle.position;
  // While landing it faces along its curve from where it is now, turning as
  // a long flight's curve bends. At the very end it keeps the heading it
  // arrived on, as there is no bearing from the destination to itself.
  const planeDirection = landing
    ? bearing(
        landingProgress < LANDED_AT
          ? planePosition
          : interpolateGreatCircle(puzzle.position, puzzle.destination, LANDED_AT),
        puzzle.destination
      )
    : puzzle.position.direction;

  // Before any guess the plane sits in the middle of the view, with as much
  // room ahead of it as the flight path takes up behind, so it reads as
  // mid-journey. After that the view frames the plane, its flight path and
  // the latest guess (including one still being checked), clear of the guess
  // card. Earlier guesses stay on the map but do not pull the view out.
  const latestGuess = wrongGuesses[wrongGuesses.length - 1];
  const focus = probe?.airport ?? latestGuess?.airport;
  // The guess on the plane's side of the antimeridian, so the view frames
  // the short way to it, the way the line is drawn.
  const focusLon = focus ? nearestLongitude(focus.lon, planeLon) : undefined;
  const focusLat = focus?.lat;
  // The layout the map was last framed for; null until the first framing.
  const framedFor = useRef<boolean | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!loaded || !map) return;

    // Leave the view alone when the guess is already comfortably in sight:
    // on screen, not hard against an edge, and not hidden under the top bar
    // or the guess card.
    if (
      framedFor.current === isDesktop &&
      focusLon !== undefined &&
      focusLat !== undefined
    ) {
      const { x, y } = map.project([focusLon, focusLat]);
      const { clientWidth: width, clientHeight: height } = map.getContainer();
      const underCard = isDesktop
        ? x < CARD_WIDTH_DESKTOP && y > height - CARD_HEIGHT
        : y > height - CARD_HEIGHT;
      const inSight =
        x > EDGE_MARGIN &&
        x < width - EDGE_MARGIN &&
        y > TOP_BAR_HEIGHT &&
        y < height - EDGE_MARGIN &&
        !underCard;
      if (inSight) return;
    }

    const points: number[][] =
      focusLon !== undefined && focusLat !== undefined
        ? [
            ...trackCoordinates,
            [focusLon, focusLat],
            // The marked airports stay in view: the player is choosing from them.
            ...possibleAirports.map((airport) => [nearestLongitude(airport.lon, planeLon), airport.lat]),
          ]
        : [
            ...trackCoordinates,
            // The flight path mirrored through the plane.
            ...trackCoordinates.map(([lon, lat]) => [
              2 * planeLon - lon,
              Math.max(-75, Math.min(75, 2 * planeLat - lat)),
            ]),
          ];
    const lons = points.map(([lon]) => lon);
    const lats = points.map(([, lat]) => lat);

    const bounds: [[number, number], [number, number]] = [
      [Math.min(...lons), Math.min(...lats)],
      [Math.max(...lons), Math.max(...lats)],
    ];
    const framing = {
      padding: isDesktop
        ? { top: 110, bottom: 80, left: 470, right: 80 }
        : { top: 120, bottom: 310, left: 40, right: 40 },
      maxZoom: 5.5,
    };
    map.fitBounds(bounds, { ...framing, duration: 1400 });
    framedFor.current = isDesktop;
  }, [loaded, focusLon, focusLat, isDesktop, trackCoordinates, planeLon, planeLat, possibleAirports]);

  // When the marked airports first appear, the view pulls back to show them
  // all with the plane.
  const markedKey = possibleAirports.map((airport) => airport.iata).join(",");
  useEffect(() => {
    const map = mapRef.current;
    if (!loaded || !map || !markedKey) return;

    const points = [
      [planeLon, planeLat],
      ...possibleAirports.map((airport) => [nearestLongitude(airport.lon, planeLon), airport.lat]),
    ];
    const lons = points.map(([lon]) => lon);
    const lats = points.map(([, lat]) => lat);
    const bounds: [[number, number], [number, number]] = [
      [Math.min(...lons), Math.min(...lats)],
      [Math.max(...lons), Math.max(...lats)],
    ];
    const framing = {
      padding: isDesktop
        ? { top: 110, bottom: 80, left: 470, right: 80 }
        : { top: 120, bottom: 310, left: 40, right: 40 },
      maxZoom: 5.5,
    };
    map.fitBounds(bounds, { ...framing, duration: 1400 });
    // Only when they first appear; later guesses frame as usual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, markedKey]);

  // A marked airport that has been guessed shows as the guess's pin instead.
  const guessedIatas = new Set(guesses.map((guess) => guess.airport.iata));
  const markedToShow = possibleAirports.filter((airport) => !guessedIatas.has(airport.iata));
  // While a guess is checked the others fade, and the one being checked is
  // drawn above the line reaching toward it, so the line ends under its ring.
  const checkedMarked = probe ? markedToShow.find((airport) => airport.iata === probe.airport.iata) : undefined;
  // They pop in once the map has drawn, so the pop is not lost on a blank map.
  const markedMotion = useMarkedMotion(loaded ? markedKey : "", possibleAirports.length, !!probe);
  const markedPoint = (airport: Airport, motion: { scale: number; opacity: number }) => ({
    type: "Feature" as const,
    properties: { iata: airport.iata, ...motion },
    geometry: {
      type: "Point" as const,
      coordinates: [nearestLongitude(airport.lon, planeLon), airport.lat],
    },
  });
  // The marked airport under the pointer spells out its city. Either its ring
  // or its code counts.
  const [pointedIata, setPointedIata] = useState<string | null>(null);
  const codeAt = (event: MapLayerMouseEvent) =>
    (event.originalEvent.target as Element).closest<HTMLElement>("[data-iata]")?.dataset.iata;
  // The marked airport clicked or tapped offers to be guessed; anywhere else on
  // the map takes the offer away.
  const [offeredIata, setOfferedIata] = useState<string | null>(null);
  const clickMap = (event: MapLayerMouseEvent) => {
    if ((event.originalEvent.target as Element).closest(".ds-marked-offer")) return;
    // Looked up afresh: event.features is whatever was last hovered, which is
    // out of date after a tap, when no pointer moved first.
    const rings =
      possibleAirports.length > 0
        ? mapRef.current?.queryRenderedFeatures(event.point, { layers: ["ds-marked-ring"] })
        : [];
    const iata: string | undefined = codeAt(event) ?? rings?.[0]?.properties?.iata;
    setOfferedIata(canGuess ? iata ?? null : null);
  };

  return (
    <div className="ds-map">
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={MAP_STYLE}
        initialViewState={{
          longitude: puzzle.position.lon,
          latitude: puzzle.position.lat,
          zoom: 3,
        }}
        projection={{ name: "mercator" }}
        dragRotate={false}
        touchPitch={false}
        onLoad={() => setLoaded(true)}
        interactiveLayerIds={markedToShow.length > 0 ? ["ds-marked-ring"] : []}
        onMouseMove={(event) => setPointedIata(codeAt(event) ?? event.features?.[0]?.properties?.iata ?? null)}
        onMouseOut={() => setPointedIata(null)}
        onClick={clickMap}
        onStyleData={() => {
          // Before the first draw, so the base style's greys never show.
          const map = mapRef.current?.getMap();
          if (painted.current || !map?.getLayer("water")) return;
          painted.current = true;
          paintMapColours(map);
        }}
      >
        {/* Forest, scrub, grass, farmland and snow, which the base style leaves out. */}
        <Source id="ds-terrain" type="vector" url="mapbox://mapbox.mapbox-terrain-v2">
          <Layer
            id="ds-landcover"
            type="fill"
            source-layer="landcover"
            beforeId="landuse"
            paint={{ "fill-color": LANDCOVER_COLOUR, "fill-antialias": false }}
          />
        </Source>

        <Source
          id="ds-track"
          type="geojson"
          data={{
            type: "Feature" as const,
            properties: {},
            geometry: { type: "LineString" as const, coordinates: trackCoordinates },
          }}
        >
          <Layer
            id="ds-track-line"
            type="line"
            layout={{ "line-cap": "round", "line-join": "round" }}
            paint={{
              "line-color": "#152238",
              "line-width": 2.5,
              "line-dasharray": [2, 2],
            }}
          />
        </Source>

        {/* The marked airports are drawn in the map itself, under the flight
            path and the line reaching toward a guess, so both cross over them.
            They pop in one after another, and fade while a guess is checked. */}
        {possibleAirports.length > 0 && (
          <Source
            id="ds-marked"
            type="geojson"
            data={{
              type: "FeatureCollection" as const,
              features: possibleAirports
                .map((airport, index) => ({ airport, motion: markedMotion(index) }))
                .filter(({ airport }) => airport !== checkedMarked && !guessedIatas.has(airport.iata))
                .map(({ airport, motion }) => markedPoint(airport, motion)),
            }}
          >
            <Layer
              id="ds-marked-ring"
              type="circle"
              beforeId="ds-track-line"
              paint={MARKED_RING}
            />
          </Source>
        )}

        {probe && (
          <Source
            id="ds-probe"
            type="geojson"
            data={{
              type: "Feature" as const,
              properties: {},
              geometry: { type: "LineString" as const, coordinates: probeCoordinates },
            }}
          >
            {/* A soft green halo under the line once the guess is right. */}
            <Layer
              id="ds-probe-glow"
              type="line"
              layout={{ "line-cap": "round", "line-join": "round" }}
              paint={{
                "line-color": "#6FBF8F",
                "line-width": 16,
                "line-blur": 8,
                "line-opacity": probe.state === "correct" ? 0.7 : 0,
                "line-opacity-transition": { duration: 500, delay: 0 },
              }}
            />
            <Layer
              id="ds-probe-line"
              type="line"
              layout={{ "line-cap": "round", "line-join": "round" }}
              paint={{
                "line-color": PROBE_COLOUR[probe.state],
                "line-color-transition": { duration: 400, delay: 0 },
                "line-width": probe.state === "correct" ? 4.5 : 3,
                "line-width-transition": { duration: 400, delay: 0 },
                "line-opacity": probe.state === "wrong" ? 0.6 : 1,
                "line-dasharray": probe.state === "correct" ? [1, 0] : [0.5, 2],
              }}
            />
          </Source>
        )}

        {/* The marked airport being checked, drawn above the line reaching
            toward it. */}
        {checkedMarked && (
          <Source
            id="ds-marked-checked"
            type="geojson"
            data={markedPoint(checkedMarked, { scale: 1, opacity: 1 })}
          >
            <Layer id="ds-marked-checked-ring" type="circle" paint={MARKED_RING} />
          </Source>
        )}

        {/* The marked airports' codes, styled like the guesses'. */}
        {possibleAirports.map((airport, index) => {
          if (!markedToShow.includes(airport)) return null;
          const motion = airport === checkedMarked ? { scale: 1, opacity: 1 } : markedMotion(index);
          if (motion.opacity === 0) return null;
          const isOffered = canGuess && airport.iata === offeredIata;
          return (
            <Marker
              key={airport.iata}
              longitude={nearestLongitude(airport.lon, planeLon)}
              latitude={airport.lat}
              anchor="bottom"
              offset={[0, -10]}
              // Always set: Marker does not clear a style that is taken away. The
              // offer also clears a guess pin raised by its hover (daily.css).
              style={{ zIndex: isOffered ? 6 : airport.iata === pointedIata ? 1 : 0 }}
            >
              {isOffered ? (
                <div className="ds-marked-offer">
                  <span className="ds-marked-offer__name">{`${airport.iata} – ${airport.city}`}</span>
                  <button
                    type="button"
                    className="ds-button ds-button--red ds-marked-offer__guess"
                    onClick={() => {
                      setOfferedIata(null);
                      onGuessMarked(airport);
                    }}
                  >
                    {dt("guess")}
                  </button>
                </div>
              ) : (
                <span
                  className="ds-marked-code"
                  data-iata={airport.iata}
                  style={{ opacity: motion.opacity, transform: `scale(${motion.scale})` }}
                >
                  {airport.iata === pointedIata ? `${airport.iata} – ${airport.city}` : airport.iata}
                </span>
              )}
            </Marker>
          );
        })}

        <Marker
          longitude={trackCoordinates[0][0]}
          latitude={puzzle.origin.lat}
          anchor="center"
        >
          <div className="ds-pin ds-pin--origin">
            <span className="ds-pin__dot" />
            <span className="ds-pin__label">{puzzle.origin.iata}</span>
          </div>
        </Marker>

        {wrongGuesses.map((guess, index) => (
          <Marker
            key={guess.airport.iata}
            longitude={nearestLongitude(guess.airport.lon, planeLon)}
            latitude={guess.airport.lat}
            anchor="center"
          >
            <button
              type="button"
              className={`ds-pin ds-pin--guess ds-pin--button ds-pin--${guess.band}`}
              aria-label={dt("guessPin", {
                number: index + 1,
                city: guess.airport.city,
                distance: formatDistanceAway(guess.distanceMiles),
              })}
              onClick={() => onGuessClick(guess)}
            >
              <span className="ds-pin__dot">{index + 1}</span>
              <span className="ds-pin__label">{guess.airport.iata}</span>
              {/* Shown on hover, where there is a pointer to hover with. */}
              <span className="ds-pin__tip" aria-hidden="true">
                {formatDistanceAway(guess.distanceMiles)}
              </span>
            </button>
          </Marker>
        ))}

        {probe?.state === "correct" && (
          <Marker
            longitude={nearestLongitude(probe.airport.lon, planeLon)}
            latitude={probe.airport.lat}
            anchor="center"
          >
            <div className="ds-pin ds-pin--guess ds-pin--correct">
              <span className="ds-pin__dot" />
              <span className="ds-pin__label">{probe.airport.iata}</span>
            </div>
          </Marker>
        )}

        <Marker
          longitude={nearestLongitude(planePosition.lon, planeLon)}
          latitude={planePosition.lat}
          anchor="center"
        >
          <div
            className="ds-plane"
            style={{ transform: `rotate(${planeDirection - 90}deg)` }}
          >
            <PlaneIcon size={30} />
          </div>
          {asking && (
            // Runs out toward the side the plane is heading, so the bubble
            // never sits on the flight path behind it.
            <span
              className={`ds-plane-bubble ${
                Math.sin((planeDirection * Math.PI) / 180) < 0
                  ? "ds-plane-bubble--left"
                  : ""
              }`}
            >
              {dt("question")}
            </span>
          )}
        </Marker>
      </Map>
    </div>
  );
};
